import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CircleCheck, CircleAlert, X } from 'lucide-react';
import { useT } from '../i18n';

interface ToastInput {
  message: string;
  tone?: 'success' | 'error' | 'info';
  action?: { label: string; onClick: () => void };
  /** ms; default 5000 (§8.4). */
  duration?: number;
}
interface ToastItem extends ToastInput {
  id: number;
}

interface ToastApi {
  show: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback((input: ToastInput) => {
    const id = ++seq.current;
    // One toast at a time keeps the undo target unambiguous.
    setItems([{ ...input, id }]);
    return id;
  }, []);
  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* No document during server rendering (tests): render no toast region there. */}
      {typeof document !== 'undefined' &&
        createPortal(
        <div className="toast-region" role="status" aria-live="polite" aria-atomic="true">
          {items.map((it) => (
            <ToastView key={it.id} item={it} onDone={() => dismiss(it.id)} />
          ))}
        </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

/** The undo window pauses while the toast has keyboard focus or hover (§11.3 time limits). */
function ToastView({ item, onDone }: { item: ToastItem; onDone: () => void }) {
  const t = useT();
  const [paused, setPaused] = useState(false);
  const remaining = useRef(item.duration ?? 5000);
  const started = useRef(Date.now());
  useEffect(() => {
    if (paused) return;
    started.current = Date.now();
    const id = window.setTimeout(onDone, remaining.current);
    return () => {
      window.clearTimeout(id);
      remaining.current -= Date.now() - started.current;
    };
  }, [paused, onDone]);
  const Icon = item.tone === 'error' ? CircleAlert : CircleCheck;
  return (
    <div
      className="toast"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="toast__msg">
        <Icon size={18} aria-hidden="true" color={item.tone === 'error' ? '#ff8a80' : '#6fd3a0'} />
        {item.message}
      </span>
      {item.action && (
        <button
          type="button"
          className="toast__action"
          onClick={() => {
            item.action?.onClick();
            onDone();
          }}
        >
          {item.action.label}
        </button>
      )}
      <button type="button" className="toast__close" onClick={onDone} aria-label={t('common.close')}>
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

export function useToast(): ToastApi {
  const v = useContext(ToastContext);
  if (!v) throw new Error('useToast outside ToastProvider');
  return v;
}
